import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { UserUpcomingContribution } from "../../interface/UserDashboard.interface";
import { CalendarToday, HourglassEmpty, Schedule, Error } from "@mui/icons-material";
import styles from '../../styles/scoped/user/UserHome.module.css';
import { formatAmount } from '../../utils/utilities';

interface UserNextContributionsViewProps {
    contributions: UserUpcomingContribution[];
}

export function UserNextContributionsView({ contributions }: UserNextContributionsViewProps) {
    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'MISSING':
            case 'PARTIAL':
            case 'OVERDUE':
                return <Error className={`${styles.statusIcon} ${styles.overdue}`} />;
            case 'PENDING':
            default:
                return <Schedule className={`${styles.statusIcon} ${styles.pending}`} />;
        }
    };

    if (!contributions || contributions.length === 0) {
        return (
            <div className={styles.contributionTableEmpty}>
                <HourglassEmpty className={styles.contributionTableEmptyIcon} />
                <p className={styles.contributionTableEmptyText}>
                    Aucun paiement à venir pour le moment
                </p>
            </div>
        );
    }

    return (
        <TableContainer
            component={Paper}
            className={`${styles.contributionTableContainer} ${styles.contributionTable}`}
        >
            <div className={styles.contributionScrollContainer}>
                <Table sx={{ minWidth: 300 }} size="small" aria-label="prochaines contributions">
                    <TableHead className={styles.contributionTableHead}>
                        <TableRow>
                            <TableCell
                                align="left"
                                className={styles.contributionTableHeadCell}
                            >
                                <CalendarToday sx={{ mr: 1, fontSize: '1rem' }} />
                                Période
                            </TableCell>
                            <TableCell
                                align="left"
                                className={styles.contributionTableHeadCell}
                            >
                                Montant restant
                            </TableCell>
                            <TableCell
                                align="left"
                                className={styles.contributionTableHeadCell}
                            >
                                Échéance
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody className={styles.contributionTableBody}>
                        {contributions.map((contribution, index) => (
                            <TableRow
                                key={contribution.uuid}
                                className={`${styles.contributionTableRow} ${styles[`status${contribution.status.charAt(0).toUpperCase() + contribution.status.slice(1)}`]}`}
                                style={{ animationDelay: `${index * 0.1}s` }}
                            >
                                <TableCell
                                    component="th"
                                    scope="row"
                                    className={`${styles.contributionTableCell} ${styles.monthCell}`}
                                >
                                    {getStatusIcon(contribution.status)}
                                    {contribution.formattedPeriod}
                                </TableCell>
                                <TableCell
                                    component="th"
                                    scope="row"
                                    className={`${styles.contributionTableCell} ${styles.amountCell}`}
                                >
                                    <span className={styles.contributionAmountBadge}>
                                        {formatAmount(contribution.remainingAmount)}
                                    </span>
                                </TableCell>
                                <TableCell
                                    component="th"
                                    scope="row"
                                    className={`${styles.contributionTableCell} ${styles.dueDateCell}`}
                                >
                                    <span className={`${styles.dueDateBadge} ${(contribution.status === 'MISSING' || contribution.status === 'PARTIAL') ? styles.overdue : ''}`}>
                                        {contribution.formattedDueDate}
                                        {contribution.daysUntilDue !== undefined && contribution.daysUntilDue > 0 && (
                                            <small> (dans {contribution.daysUntilDue} jour{contribution.daysUntilDue > 1 ? 's' : ''})</small>
                                        )}
                                    </span>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        </TableContainer>
    );
}
